import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { z } from "zod";
import { MarketingShell } from "@/components/MarketingShell";
import { PageContainer } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { CheckCircle2, MailX } from "lucide-react";

const searchSchema = z.object({
  id: z.string().uuid().optional(),
  email: z.string().email().optional(),
});

export const Route = createFileRoute("/marketing/unsubscribe")({
  validateSearch: (s) => searchSchema.parse(s),
  head: () => ({
    meta: [
      { title: "Unsubscribe — MissionReady" },
      { name: "description", content: "Unsubscribe from MissionReady marketing emails." },
      { property: "og:title", content: "Unsubscribe — MissionReady" },
      { property: "og:description", content: "Manage your MissionReady marketing email subscription." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: UnsubscribePage,
});

function UnsubscribePage() {
  const { id, email } = Route.useSearch();
  const [status, setStatus] = useState<"idle" | "working" | "done" | "error">("idle");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!id || !email) return;
    setStatus("working");
    fetch("/api/public/marketing/unsubscribe", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ id, email }),
    })
      .then(async (r) => {
        const data = (await r.json().catch(() => ({}))) as { ok?: boolean; error?: string };
        if (!r.ok || !data.ok) throw new Error(data.error || "Unable to unsubscribe.");
        setStatus("done");
      })
      .catch((err) => {
        setStatus("error");
        setError(err instanceof Error ? err.message : "Unable to unsubscribe.");
      });
  }, [id, email]);

  return (
    <MarketingShell>
      <PageContainer className="py-20">
        <div className="mx-auto max-w-xl text-center">
          {!id || !email ? (
            <>
              <MailX className="mx-auto h-10 w-10 text-muted-foreground" />
              <h1 className="mt-4 text-2xl font-bold">Unsubscribe link is missing details</h1>
              <p className="mt-2 text-muted-foreground">
                Use the unsubscribe link at the bottom of any MissionReady email, or contact us and we&apos;ll opt
                you out manually.
              </p>
              <Button asChild className="mt-6"><Link to="/contact">Contact us</Link></Button>
            </>
          ) : status === "working" ? (
            <>
              <h1 className="text-2xl font-bold">Unsubscribing…</h1>
              <p className="mt-2 text-muted-foreground">One moment.</p>
            </>
          ) : status === "done" ? (
            <>
              <CheckCircle2 className="mx-auto h-10 w-10 text-primary" />
              <h1 className="mt-4 text-2xl font-bold">You&apos;re unsubscribed</h1>
              <p className="mt-2 text-muted-foreground">
                <span className="font-medium text-foreground">{email}</span> won&apos;t receive any more MissionReady
                marketing emails. Sorry to see you go.
              </p>
              <div className="mt-6 flex flex-wrap justify-center gap-3">
                <Button asChild variant="outline"><Link to="/">Back to home</Link></Button>
                <Button asChild><Link to="/contact">Send feedback</Link></Button>
              </div>
            </>
          ) : status === "error" ? (
            <>
              <MailX className="mx-auto h-10 w-10 text-destructive" />
              <h1 className="mt-4 text-2xl font-bold">We couldn&apos;t unsubscribe you</h1>
              <p className="mt-2 text-muted-foreground">{error}</p>
              <p className="mt-2 text-sm text-muted-foreground">
                Email <a className="underline" href="mailto:Stilesterk.armedservices@gmail.com">Stilesterk.armedservices@gmail.com</a> and we&apos;ll remove you manually.
              </p>
            </>
          ) : (
            <><h1 className="text-2xl font-bold">Manage your email subscription</h1><p className="text-muted-foreground">Preparing…</p></>
          )}
        </div>
      </PageContainer>
    </MarketingShell>
  );
}
